import type { FullAirHandlerUnit } from "@eisox/api/core-api";
import { withForm } from "@eisox/form";

import {
  CO2_STEP,
  HUMIDITY_STEP,
  MAXIMUM_CO2_SETPOINT,
  MAXIMUM_FLOW_SETPOINT,
  MAXIMUM_HUMIDITY_SETPOINT,
  MAXIMUM_PRESSURE_SETPOINT,
  MINIMUM_CO2_SETPOINT,
  MINIMUM_FLOW_SETPOINT,
  MINIMUM_HUMIDITY_SETPOINT,
  MINIMUM_PRESSURE_SETPOINT,
  PRESSURE_STEP,
  STEP,
} from "~/constants/air-handler-unit.constant";
import { m } from "~/paraglide/messages";

import {
  CO2_SETPOINTS,
  FLOW_SETPOINTS,
  flowFieldName,
  getFlowSetpointDefaultValues,
  getPressureSetpointDefaultValues,
  getSetpointLabel,
  HUMIDITY_SETPOINTS,
  PRESSURE_SETPOINTS,
  pressureFieldName,
  SETPOINT_DUCTS,
  SETPOINT_PERIOD_TITLE,
  SETPOINT_SEASONS,
  setpointFormOptions,
  widenBoundsTo,
  type BoundFieldName,
  type CO2SetpointKey,
  type FlowFieldName,
  type HumiditySetpointKey,
  type PressureFieldName,
  type Season,
  type SetpointDuct,
  type SetpointQuantity,
} from "../helpers";
import { DiscrepancyLabel } from "./discrepancy-slider";
import { SetpointBoundField } from "./setpoint-bound-field";
import { SetpointSeasonBlock } from "./setpoint-season-block";

const DUCT_TITLE: Record<SetpointDuct, () => string> = {
  supply: m.supply,
  return: m.return,
};

interface QuantityPoint {
  name: PressureFieldName | FlowFieldName | HumiditySetpointKey | CO2SetpointKey;
  season: Season;
  label: React.ReactNode;
}

interface QuantityConfig {
  title: () => string;
  min: BoundFieldName;
  max: BoundFieldName;
  unit: string;
  range: { min: number; max: number };
  step: number;
  points: (airHandlerUnit: FullAirHandlerUnit) => QuantityPoint[];
}

// A duct the CTA does not declare carries no point: a slider offered for it would be settable,
// saved and dropped on the way out, the form resetting as though the value had left.
const QUANTITIES: Record<SetpointQuantity, QuantityConfig> = {
  pressure: {
    title: m.pressure,
    min: "pressureMinSetpoint",
    max: "pressureMaxSetpoint",
    unit: "Pa",
    range: { min: MINIMUM_PRESSURE_SETPOINT, max: MAXIMUM_PRESSURE_SETPOINT },
    step: PRESSURE_STEP,
    points: airHandlerUnit => {
      const reported = getPressureSetpointDefaultValues(airHandlerUnit.flows);
      return SETPOINT_DUCTS.flatMap(type =>
        PRESSURE_SETPOINTS.filter(({ key }) => reported[pressureFieldName(type, key)] !== undefined).map(
          ({ key, season, period }) => ({
            name: pressureFieldName(type, key),
            season,
            label: `${DUCT_TITLE[type]()} — ${SETPOINT_PERIOD_TITLE[period]()} (Pa)`,
          }),
        ),
      );
    },
  },
  humidity: {
    title: m.hygrometry,
    min: "humidityMinSetpoint",
    max: "humidityMaxSetpoint",
    unit: "%",
    range: { min: MINIMUM_HUMIDITY_SETPOINT, max: MAXIMUM_HUMIDITY_SETPOINT },
    step: HUMIDITY_STEP,
    points: airHandlerUnit =>
      HUMIDITY_SETPOINTS.filter(({ key }) => airHandlerUnit[key] !== undefined).map(({ key, season, period }) => ({
        name: key,
        season,
        label: <DiscrepancyLabel label={`${SETPOINT_PERIOD_TITLE[period]()} (%)`} property={key} />,
      })),
  },
  flow: {
    title: m.flows,
    min: "flowMinSetpoint",
    max: "flowMaxSetpoint",
    unit: "m³/h",
    range: { min: MINIMUM_FLOW_SETPOINT, max: MAXIMUM_FLOW_SETPOINT },
    step: STEP,
    points: airHandlerUnit => {
      const reported = getFlowSetpointDefaultValues(airHandlerUnit.flows);
      return SETPOINT_DUCTS.flatMap(type =>
        FLOW_SETPOINTS.filter(({ key }) => reported[flowFieldName(type, key)] !== undefined).map(
          ({ key, season, period }) => ({
            name: flowFieldName(type, key),
            season,
            label: `${DUCT_TITLE[type]()} — ${SETPOINT_PERIOD_TITLE[period]()} (m³/h)`,
          }),
        ),
      );
    },
  },
  co2: {
    title: m.co2,
    min: "co2MinSetpoint",
    max: "co2MaxSetpoint",
    unit: "ppm",
    range: { min: MINIMUM_CO2_SETPOINT, max: MAXIMUM_CO2_SETPOINT },
    step: CO2_STEP,
    points: airHandlerUnit =>
      CO2_SETPOINTS.filter(({ key }) => airHandlerUnit[key] !== undefined).map(({ key, season, period }) => ({
        name: key,
        season,
        label: <DiscrepancyLabel label={`${SETPOINT_PERIOD_TITLE[period]()} (ppm)`} property={key} />,
      })),
  },
};

export const getSetpointQuantityTitle = (quantity: SetpointQuantity) => QUANTITIES[quantity].title();

/** A quantity gets its section as soon as the CTA reports one of its setpoints. */
export const hasSetpointQuantity = (quantity: SetpointQuantity, airHandlerUnit: FullAirHandlerUnit) =>
  QUANTITIES[quantity].points(airHandlerUnit).length > 0;

/**
 * Pressure, humidity, flow and CO2 share one shape: their pair of bounds, then one slider per
 * setpoint and season, whose range follows the bounds as the installer moves them.
 */
const SetpointQuantitySection = withForm({
  ...setpointFormOptions,
  props: {} as { quantity: SetpointQuantity; airHandlerUnit: FullAirHandlerUnit },
  render: function Render({ form, quantity, airHandlerUnit }) {
    const { min, max, unit, range, step, points } = QUANTITIES[quantity];
    const quantityPoints = points(airHandlerUnit);

    return (
      <form.Subscribe selector={state => ({ min: state.values[min], max: state.values[max] })}>
        {bounds => (
          <div className="gap-2.5 flex flex-col">
            <SetpointBoundField
              form={form}
              name={min}
              other={max}
              side="min"
              label={<DiscrepancyLabel label={getSetpointLabel(min, unit)} property={min} />}
              range={range}
              step={step}
            />
            <SetpointBoundField
              form={form}
              name={max}
              other={min}
              side="max"
              label={<DiscrepancyLabel label={getSetpointLabel(max, unit)} property={max} />}
              range={range}
              step={step}
            />
            {SETPOINT_SEASONS.filter(season => quantityPoints.some(point => point.season === season)).map(season => (
              <SetpointSeasonBlock key={season} season={season}>
                {quantityPoints
                  .filter(point => point.season === season)
                  .map(point => (
                    <form.AppField key={point.name} name={point.name}>
                      {field => {
                        const sliderRange = widenBoundsTo(
                          { min: bounds.min ?? range.min, max: bounds.max ?? range.max },
                          field.state.value,
                        );

                        return (
                          <field.Slider
                            classNames={{ control: "w-full", value: "w-16 justify-end" }}
                            label={point.label}
                            min={sliderRange.min}
                            max={sliderRange.max}
                            step={step}
                          />
                        );
                      }}
                    </form.AppField>
                  ))}
              </SetpointSeasonBlock>
            ))}
          </div>
        )}
      </form.Subscribe>
    );
  },
});

export { SetpointQuantitySection };
